import { useRef } from "react";
import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { tests as testsCache } from "../local/db";

// #1750 — resolves the ONE test RideList uses to decide whether a class
// routes to the per-ride list or the class grid (sheetLayout: "class-grid"),
// and that ClassGrid then builds its COLUMNS from (test.observations grouped
// by .group). #462 / useScoreSheet.js's own comment (twice) is explicit:
// "ride.testId is the ONLY source. Never fall back to klass.testId: in a
// Test-of-Choice class the class test is meaningless." Routing gets no
// exemption from that rule — a wrong representative test here means a wrong
// column grid for every row, not just a wrong redirect — so this prefers the
// first ride's testId and only falls back to klass.testId when no ride has
// loaded yet (nothing else to route on). Cache-first (#421) like every other
// scribe-fe read, so the routing decision survives offline instead of
// stalling behind a network call mid-show.
export function useClassGridTest(klass, rides) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  const testId = rides?.[0]?.testId ?? klass?.testId ?? null;
  const { data: test, loading } = useCachedResource(
    async () => {
      if (!testId) return null;
      return (await testsCache.get(testId)) ?? undefined;
    },
    async () => {
      if (!testId) return null;
      const t = await api.tests.getTest(testId).then(unwrap);
      if (t) await testsCache.put(t);
      return t;
    },
    [api, testId],
    online,
  );

  // #2232 — root cause of the RideList<->grid ping-pong: on the SAME render
  // where `testId` first changes (rides[0].testId resolving from null once
  // useRidesForClass delivers klass/rides), useCachedResource's `loading`
  // still holds whatever the PREVIOUS testId's resolve left behind (`false`,
  // once ANY resolve has ever happened) — no synchronous fix inside
  // useCachedResource can correct this for THIS SPECIFIC render, because
  // React doesn't re-render mid-effect-flush: its effect for the new testId,
  // and RideList's own redirect effect right after it in the same commit,
  // both still read the closure values captured when this render ran, before
  // either effect executes.
  //
  // Fixed here, not in useCachedResource: an #2232 attempt to also reset
  // `loading` inside useCachedResource itself on a real deps/online change
  // was denied by review (see that file's git history) because every warm-
  // cache consumer that conditionally unmounts its content on `loading`
  // (ClassGrid.jsx, RideList.jsx, ClassList.jsx, and announcer-fe's
  // equivalents) would then blank already-rendered, cached content — and
  // drop scribe's in-progress draft state with it — on every ordinary
  // `online` flap, not just on a cold-cache classify. This guard is the
  // narrower fix: it only forces `loading`/`test` for the ONE render where
  // `testId` itself just changed, which is the render RideList's redirect
  // effect actually reads on a cold load; it does not touch any other
  // render or any other hook.
  //
  // Comparing `testId` against a ref updated during render, rather than
  // `useState` + a render-time `setState` call (React's actual documented
  // pattern for "derive state from a changed prop", see
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders),
  // is an intentional shortcut: a plain ref read/write during render has no
  // render-count guarantee and is silently defeated by a discarded or
  // double-invoked render pass (e.g. under `StrictMode`). There is no live
  // defect today because scribe-fe mounts via `createRoot` with no
  // `StrictMode` (apps/scribe-fe/src/main.jsx) — but if `StrictMode` is ever
  // added here, this ref must be converted to the `useState`-based pattern
  // linked above, or this guard silently stops working.
  //
  // This is the shared root for both of useClassGridTest's callers —
  // RideList.jsx's redirect effect and ClassGrid.jsx's own back-redirect
  // both consume it, so neither one needs its own copy of this guard.
  const lastRequestedTestIdRef = useRef(testId);
  const testIdJustChanged = lastRequestedTestIdRef.current !== testId;
  lastRequestedTestIdRef.current = testId;

  return {
    test: testIdJustChanged ? null : test,
    loading: loading || testIdJustChanged,
  };
}
